import { useState, useEffect } from 'react';
import { Accordion } from '../../../common/SharedComponents';
import { ApplicationApi } from '@apply/api';
import { applicationInsights } from '../../../ApplicationInsights';
import { getLoanTypeShortName } from '../../../loans/common/LoanFunctions';
import { BfiDataGrid, Column } from '../../../common/SharedComponents';
import { formatDateTime } from '@apply/ui';
import { GridSettingType } from '@apply/constants';
import store from '@apply/adminStore';
import { applicationActions } from '@apply/adminStore';
import ActionSheet from 'devextreme-react/action-sheet';
import { useSelector } from 'react-redux';

const IncompleteAppsActionButton = ({ data, parentElementId }) => {
    const { dispatch } = store;

    const [actionData, set_actionData] = useState({
        visible: false,
        target: null,
        items: [],
    });

    const onActionClicked = (event, applicationId) => {
        event.preventDefault();
        event.stopPropagation();

        const actions = [];

        actions.push({
            id: 0,
            text: 'Review',
            applicationId,
            stylingMode: 'contained',
        });

        actions.push({
            id: 1,
            text: 'Lost/Delete',
            applicationId,
            type: 'danger',
            icon: 'remove',
        });

        var data = {
            items: actions,
            target: `#${event.target.id}`,
            visible: true,
        };
        set_actionData(data);
    };

    const onActionItemClick = (e) => {
        set_actionData({ ...actionData, visible: false });
        switch (e.itemData.id) {
            case 0:
                dispatch(
                    applicationActions.setIncompleteReviewModal({
                        incompleteReviewModal: {
                            isOpen: true,
                            id: e.itemData.applicationId,
                        },
                    })
                );
                break;
            case 1:
                dispatch(
                    applicationActions.setDeclineOrRecycleModal({
                        declineOrRecycleModal: {
                            isOpen: true,
                            id: e.itemData.applicationId,
                        },
                    })
                );
                break;
        }
    };

    const onActionSheetVisibleChange = (isVisible) => {
        if (isVisible !== actionData?.visible) {
            set_actionData({ ...actionData, visible: isVisible });
        }
    };

    if (!data) {
        return null;
    }

    return (
        <>
            <button
                id={`action_incomplete_${parentElementId ?? ''}_${data.id}`}
                className='gridButton'
                title='Click to see options'
                onClick={(event) => onActionClicked(event, data.id)}
            >
                ...
            </button>
            <ActionSheet
                title='Choose action'
                usePopover={true}
                visible={actionData.visible}
                target={actionData.target}
                items={actionData.items}
                onItemClick={onActionItemClick}
                onVisibleChange={onActionSheetVisibleChange}
                showTitle={false}
            />
        </>
    );
};

const IncompleteAppsTable = ({ gridStore }) => {
    const searchState = useSelector((state) => state.searchState);
    const applicationApi = new ApplicationApi.ApplicationApi(applicationInsights);
    const { dispatch } = store;
    const dataFunction = (criteria) => applicationApi.getGrid_Incomplete(criteria);

    const [dataStore, set_dataStore] = useState(gridStore?.store);

    const style = {
        percent: { color: 'white', textAlign: 'center', width: '100%' },
    };

    useEffect(() => {
        if (gridStore) {
            gridStore.setOnLoad('id', dataFunction, searchState?.searchText?.incompleteApplications);
            set_dataStore(gridStore.store);
        }
    }, [gridStore]);

    const onLoanTypeCellRender = (e) => {
        return getLoanTypeShortName(e.data.loanTypeId);
    };

    const onLastUpdatedRender = (e) => {
        if (!e.value) {
            return null;
        }
        return formatDateTime(e.value);
    };

    const onPercentCompleteRender = (e) => {
        if (!e.value) {
            return null;
        }
        const width = e.value.toString() + '%';
        return (
            <div style={style.percent}>
                <div style={{ width: width, backgroundColor: '#2a5fa0' }}>{e.value}</div>
            </div>
        );
    };

    const onOpenReviewModal = (e) => {
        if (e.rowType !== 'data' || e.column.type === 'detailExpand' || e.column.type === 'actions') {
            return;
        }
        dispatch(
            applicationActions.setIncompleteReviewModal({
                incompleteReviewModal: { isOpen: true, id: e.data.id },
            })
        );
    };

    const actionCellRender = (e) => {
        return (
            <IncompleteAppsActionButton
                data={e.data}
                parentElementId='incompleteAppId'
            />
        );
    };

    return (
        <Accordion
            title={'Incomplete Applications'}
            subTitle='Applications that were created but never submitted.'
        >
            <div className='card'>
                <div className='card-section'>
                    <BfiDataGrid
                        gridType={GridSettingType.Applications_Incomplete}
                        defaultPageSize={25}
                        dataStore={dataStore}
                        applicationInsights={applicationInsights}
                        onCellDblClick={onOpenReviewModal}
                        allowColumnResizing={true}
                        allowReordering={true}
                    >
                        <Column
                            dataField='borrowerName'
                            caption='Borrower Name'
                            alignment='left'
                        />
                        <Column
                            dataField='borrowerEmail'
                            caption='Email'
                            alignment='left'
                            width={300}
                        />
                        <Column
                            dataField='borrowerEntity'
                            caption='Borrower Entity'
                            alignment='left'
                        />
                        <Column
                            dataField='loanTypeId'
                            caption='Loan Type'
                            alignment='left'
                            width={200}
                            cellRender={onLoanTypeCellRender}
                        />
                        <Column
                            dataField='updatedAt'
                            caption='Last Updated'
                            width={150}
                            cellRender={onLastUpdatedRender}
                        />
                        <Column
                            dataField='percentComplete'
                            caption='% Compl.'
                            width={90}
                            allowSorting={false}
                            cellRender={onPercentCompleteRender}
                        />
                        <Column
                            dataField='pointOfContact'
                            caption='POC'
                            dataType='string'
                            width={250}
                        />
                        <Column
                            alignment='center'
                            cellRender={actionCellRender}
                            width={50}
                            allowReordering={false}
                            allowSorting={false}
                            allowResizing={false}
                            type='actions'
                            fixed={true}
                            fixedPosition={'right'}
                        />
                    </BfiDataGrid>
                </div>
            </div>
        </Accordion>
    );
};

export default IncompleteAppsTable;
